import { useMemo } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import { traineeMeasurementsActions } from "@fitness-app/app-store";
import {
  MeasurementType,
  type BodyMeasurements,
  type ClientPhoto,
  type Measurement,
  type WeightMeasurement,
} from "@fitness-app/data-models/entities/Measurement";
import { getBodyMeasurementsWithVariation } from "@fitness-app/utils/src/measurements/getBodyMeasurementsWithVariation";

import { traineeKeys, useTraineeId, useTraineeProfile } from "~/modules/Trainee/TraineeProfile/scope";
import { supabase, useAppDispatch } from "~/store/initializeStore";

// Ten sam rozmiar strony co `measurementsPageSize` w slice'ie traineeMeasurements.
export const MEASUREMENTS_PAGE_SIZE = 25;

export const traineeMeasurementKeys = {
  bodyAndWeight: (traineeId: string) => [...traineeKeys.measurements(traineeId), "bodyAndWeight"] as const,
  bodyPhotos: (traineeId: string) => [...traineeKeys.measurements(traineeId), "bodyPhotos", "list"] as const,
  allBodyPhotos: (traineeId: string) => [...traineeKeys.measurements(traineeId), "bodyPhotos", "all"] as const,
  latestWeight: (traineeId: string) => [...traineeKeys.measurements(traineeId), "latestWeight"] as const,
  forDay: (traineeId: string, type: Measurement["type"], eventDate: string) =>
    [...traineeKeys.measurements(traineeId), "day", type, eventDate] as const,
  forRange: (traineeId: string, type: Measurement["type"], dateStart: string, dateEnd: string) =>
    [...traineeKeys.measurements(traineeId), "range", type, dateStart, dateEnd] as const,
  // TRE-2413: pod pomiarami, żeby odświeżenie nawodnienia odświeżało też rozbicie na napoje.
  hydrationEntries: (traineeId: string, dayStart: string, dayEnd: string) =>
    [...traineeKeys.measurements(traineeId), "hydrationEntries", dayStart, dayEnd] as const,
};

interface Page<T> {
  data: T[];
  currentPage: number;
  totalPages: number;
}

const fetchMeasurementsPage = async <T>(
  traineeId: string,
  types: Measurement["type"][],
  page: number,
): Promise<Page<T>> => {
  const start = (page - 1) * MEASUREMENTS_PAGE_SIZE;
  const stop = page * MEASUREMENTS_PAGE_SIZE - 1;

  const { error, data, count } = await supabase
    .from("measurement")
    .select("*", { count: "exact" })
    .order("eventTimestamp", { ascending: false })
    .eq("traineeId", traineeId)
    .in("type", types)
    .range(start, stop)
    .limit(MEASUREMENTS_PAGE_SIZE)
    .returns<T[]>();

  if (error) {
    throw new Error(error.message);
  }

  return {
    data: data || [],
    currentPage: page,
    totalPages: count ? Math.ceil(count / MEASUREMENTS_PAGE_SIZE) : 0,
  };
};

const getNextPageParam = (lastPage: Page<unknown>) =>
  lastPage.currentPage < lastPage.totalPages && lastPage.totalPages > 1 ? lastPage.currentPage + 1 : undefined;

/** Pomiary wagi i obwodów stronami po 25 (to samo co `fetchBodyAndWeightMeasurements`). */
export const useTraineeBodyAndWeightMeasurements = (traineeId: string) => {
  const query = useInfiniteQuery({
    queryKey: traineeMeasurementKeys.bodyAndWeight(traineeId),
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchMeasurementsPage<BodyMeasurements | WeightMeasurement>(
        traineeId,
        [MeasurementType.BodyMeasurements, MeasurementType.WeightMeasurement],
        pageParam,
      ),
    getNextPageParam,
  });

  const measurements = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  return { ...query, measurements };
};

/** Zdjęcia sylwetki stronami po 25 (to samo co `fetchTraineeBodyPhotos`). */
export const useTraineeBodyPhotosList = (traineeId: string) => {
  const query = useInfiniteQuery({
    queryKey: traineeMeasurementKeys.bodyPhotos(traineeId),
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchMeasurementsPage<ClientPhoto>(traineeId, [MeasurementType.ClientPhoto], pageParam),
    getNextPageParam,
  });

  const photos = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  return { ...query, photos };
};

/** Pomiary z tego samego dnia złączone w jeden wiersz (odpowiednik `getMergedMeasurementsByDay`). */
export const mergeMeasurementsByDay = (measurements: (BodyMeasurements | WeightMeasurement)[]) => {
  const byDate = new Map<string, Record<string, unknown>>();

  [...measurements]
    .sort((a, b) => (a.eventDate < b.eventDate ? 1 : a.eventDate > b.eventDate ? -1 : 0))
    .forEach((measurement) => {
      byDate.set(measurement.eventDate, { ...byDate.get(measurement.eventDate), ...measurement.data });
    });

  return Array.from(byDate, ([date, data]) => ({ date, ...data }));
};

/** Zdjęcia pogrupowane po dniu (odpowiednik `getGroupedPhotosByDay`). */
export const groupPhotosByDay = (photos: ClientPhoto[]) => {
  const byDate = new Map<string, ClientPhoto[]>();

  photos
    .filter((photo) => photo.id !== "-1")
    .forEach((photo) => {
      byDate.set(photo.eventDate, [...(byDate.get(photo.eventDate) ?? []), photo]);
    });

  return Array.from(byDate, ([date, groupedPhotos]) => ({ date, photos: groupedPhotos }));
};

const roundToOne = (value: number) => Math.round(value * 10) / 10;

/**
 * Ostatnia waga klientki i zmiana względem poprzedniego pomiaru. Odpowiednik selektora
 * `getLatestWeightMeasurement` (fetchLatestWeightMeasurement z fallbackiem na wagę z profilu).
 */
export const useTraineeLatestWeight = () => {
  const traineeId = useTraineeId();
  const { data: profile } = useTraineeProfile();

  const { data: latestWeights } = useQuery({
    queryKey: traineeMeasurementKeys.latestWeight(traineeId),
    queryFn: async () => {
      const { error, data } = await supabase
        .from("measurement")
        .select("*")
        .order("eventTimestamp", { ascending: false })
        .eq("traineeId", traineeId)
        .in("type", [MeasurementType.WeightMeasurement])
        .limit(2)
        .returns<WeightMeasurement[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
  });

  return useMemo(() => {
    const [lastWeight, previousWeight] = latestWeights ?? [];

    if (lastWeight) {
      return {
        date: dayjs(lastWeight.eventTimestamp),
        weight: lastWeight.data.weight,
        change: previousWeight ? roundToOne(lastWeight.data.weight - previousWeight.data.weight) : null,
      };
    }

    return {
      date: profile ? dayjs(profile.createdAt) : null,
      weight: profile ? profile.weight : null,
      change: null,
    };
  }, [latestWeights, profile]);
};

/** Ostatnie obwody z wczytanej listy pomiarów (odpowiednik `getLatestBodyMeasurements`). */
export const useTraineeLatestBodyMeasurements = (traineeId: string) => {
  const { measurements } = useTraineeBodyAndWeightMeasurements(traineeId);

  return useMemo(
    () =>
      getBodyMeasurementsWithVariation(
        measurements.filter(
          (measurement): measurement is BodyMeasurements => measurement.type === MeasurementType.BodyMeasurements,
        ),
      ),
    [measurements],
  );
};

/**
 * Pomiar z wybranego dnia dla formularza. Pobierany na żądanie (zmiana daty w formularzu),
 * a nie przez `useQuery`, żeby odświeżenie z realtime nie nadpisało wpisanych wartości.
 */
export const useFetchMeasurementForDay = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return (traineeId: string, type: Measurement["type"], eventDate: string) =>
    queryClient.fetchQuery({
      queryKey: traineeMeasurementKeys.forDay(traineeId, type, eventDate),
      staleTime: 0,
      // `fetchMeasurementForDay` nie dotyka stanu (brak reducera, brak getState), zgłasza tylko duplikaty dnia.
      queryFn: () =>
        dispatch(traineeMeasurementsActions.fetchMeasurementForDay({ traineeId, type, eventDate })).unwrap(),
    });
};

interface UpsertMeasurementPayload {
  data: WeightMeasurement["data"] | BodyMeasurements["data"];
  eventDate: string;
  id?: string | null;
  previousMeasurement?: WeightMeasurement | BodyMeasurements | null;
}

export const useUpsertTraineeMeasurement = (traineeId: string) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { data: profile } = useTraineeProfile();

  return useMutation({
    // `upsertMeasurement` bierze wszystko z argumentów (nie czyta getState).
    mutationFn: (payload: UpsertMeasurementPayload) => {
      // Profil karty wczytuje się asynchronicznie - bez userId pomiar nie miałby właściciela.
      if (!profile?.userId) {
        return Promise.reject(new Error("Trainee profile is not loaded yet"));
      }

      return dispatch(
        traineeMeasurementsActions.upsertMeasurement({ ...payload, traineeId, userId: profile.userId }),
      ).unwrap();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: traineeKeys.measurements(traineeId) }),
  });
};

export const useDeleteTraineeMeasurements = (traineeId: string) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    // `deleteMeasurement` bierze wszystko z argumentów (nie czyta getState).
    mutationFn: (ids: string[]) => dispatch(traineeMeasurementsActions.deleteMeasurement({ ids })).unwrap(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: traineeKeys.measurements(traineeId) }),
  });
};
